import React from "react";
import { Box, IconButton } from "@mui/material";
import { EditOutlined, EmojiEmotionsOutlined } from "@mui/icons-material";
import ReactHtmlParser from "html-react-parser";

import HoverablePaper from "components/Shared/HoverablePaper";
import FileIcon from "components/Shared/FileIcon";

import { useAuth } from "hooks";

import { useAddReactionToResponseMutation } from "store/services/announcementApi";
import { useAnnouncementDetailContext } from "contexts/AnnouncementDetailContext";
import { ReactionBar } from "./ReactionBar";
import { localizeDateTimeMed } from "utils/dateFormats";

const AnnouncementResponseListItem = ({ response, onEditAction }) => {

    const [openReaction, setOpenReaction] = React.useState(false);

    const { checkIfAuthor, getAuthorName } = useAuth();
    const { announcementDetail } =
        useAnnouncementDetailContext();
    const [addReaction] = useAddReactionToResponseMutation();
    const getGroupedReactions = (reactions = []) => {
        const map = new Map();

        reactions.forEach(({ emoji }) => {
            map.set(emoji, (map.get(emoji) || 0) + 1);
        });

        return Array.from(map.entries());
    };
    return (
        <Box paddingY={3} key={response?._id}>

            <div className="grid grid-cols-1 gap-4">
                <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="grid aspect-square w-8 place-content-center rounded-full border bg-blue-100 text-blue-500 dark:border-gray-400 dark:bg-gray-700 dark:text-blue-200">
                            {response?.createdBy?.name?.charAt(0)?.toUpperCase()}
                        </div>

                        <div>
                            {getAuthorName(response?.createdBy)},{" "}
                            <span className="text-sm text-gray-500 dark:text-gray-300">
                                responded {localizeDateTimeMed(response?.createdAt)}
                            </span>{" "}
                            {response?.updatedBy && (
                                <span className="text-gray-500">
                                    Edited {localizeDateTimeMed(response?.updatedAt)}
                                </span>
                            )}
                        </div>
                        <div>
                            {response?.reactions?.length > 0 && (
                                <div className="flex gap-2 mt-2 flex-wrap">
                                    {getGroupedReactions(response?.reactions).map(([emoji, count]) => (
                                        <div
                                            key={emoji}
                                            className="flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-full px-1 py-[1px]"
                                        >
                                            <span>{emoji}</span>
                                            {count > 1 && <span>{count}</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>


                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "end" }}>
                        {/* Three dot icon button */}
                        <IconButton
                            size="small"
                            onClick={() => setOpenReaction((prev) => !prev)}
                        >
                            <EmojiEmotionsOutlined />
                        </IconButton>

                        {/* Conditionally render ReactionBar when openReaction is true */}
                        {openReaction && (
                            <ReactionBar
                                onReact={(emoji) => {
                                    addReaction({
                                        responseId: response?._id,
                                        id: announcementDetail?._id,
                                        emoji,
                                    });
                                    setOpenReaction(false); // Close the ReactionBar after selecting an emoji
                                }}
                            />
                        )}
                        {checkIfAuthor(response?.createdBy) && (
                            <IconButton size="small" onClick={() => onEditAction(response)}>
                                <EditOutlined />
                            </IconButton>
                        )}
                    </Box>
                </div>

                <div className="relative">
                    <div class="absolute -top-2.5 ml-2.5 h-0 w-0 border-x-[6px] border-b-[10px] border-x-transparent  border-b-blue-100 dark:border-b-gray-700" />

                    <HoverablePaper
                        variant="outlined"
                        className="grid grid-cols-1 gap-3 px-4 py-4"
                    >
                        {response?.content && (
                            <div>{ReactHtmlParser(response?.content)}</div>
                        )}

                        {!!response?.attachments?.length && (
                            <div className="flex gap-4">
                                {response?.attachments?.map((attachment) => (
                                    <a
                                        key={attachment?._id}
                                        href={attachment?.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        title={`Open ${attachment?.name} in new tab`}
                                        className="text-primary cursor-pointer hover:underline"
                                    >
                                        <FileIcon
                                            fileName={attachment?.name}
                                            fileUrl={attachment?.url}
                                        />
                                    </a>
                                ))}
                            </div>
                        )}
                    </HoverablePaper>
                </div>
            </div>
        </Box>
    );
};

export default AnnouncementResponseListItem;